import { useState } from 'react';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSubmitStatus('success');
      setEmail('');
      setTimeout(() => setSubmitStatus('idle'), 3000);
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-neutral-900 text-white pt-12 sm:pt-16 lg:pt-20 pb-6 sm:pb-8">
      <div className="container mx-auto px-4 sm:px-6">
        {/* Newsletter Section */}
        <div className="border-b border-white/10 pb-10 sm:pb-12 lg:pb-16 mb-10 sm:mb-12">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-yellow-600/20 rounded-full mx-auto mb-4 sm:mb-6">
              <i className="ri-mail-line text-xl sm:text-2xl text-yellow-600"></i>
            </div>
            <h3 className="text-2xl sm:text-3xl lg:text-4xl font-light mb-3 sm:mb-4 tracking-wide">
              Exklusive Insights für Bewerber
            </h3>
            <p className="text-sm sm:text-base text-white/60 mb-6 sm:mb-8 font-light">
              Erhalten Sie wertvolle Informationen über Dubai-Strukturen, Investments und Insider-Wissen
            </p>

            {submitStatus === 'success' ? (
              <div className="inline-flex items-center gap-2 px-6 py-3 bg-green-500/20 border border-green-500/30 rounded-full text-green-500 text-sm sm:text-base">
                <i className="ri-check-line"></i>
                <span className="font-light">Erfolgreich angemeldet</span>
              </div>
            ) : (
              <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto">
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Ihre E-Mail-Adresse"
                    required
                    className="flex-1 px-4 sm:px-6 py-3 sm:py-4 bg-neutral-800 border border-white/10 rounded-full focus:outline-none focus:border-yellow-600/50 transition-colors text-white placeholder-white/40 font-light text-sm sm:text-base"
                  />
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-6 sm:px-8 py-3 sm:py-4 bg-yellow-600 text-black rounded-full hover:bg-yellow-500 transition-colors disabled:opacity-50 font-light whitespace-nowrap cursor-pointer text-sm sm:text-base"
                  >
                    {isSubmitting ? 'Wird gesendet...' : 'Anmelden'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Main Footer Content */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12 mb-10 sm:mb-12">
          {/* Column 1: Brand */}
          <div>
            <div className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">
              Dubai <span className="text-yellow-600">Business Club</span>
            </div>
            <p className="text-sm sm:text-base text-white/60 font-light leading-relaxed mb-4 sm:mb-6">
              Exklusiver Unternehmer-Club mit Fokus auf UAE-Strukturen, Immobilien und echtem Experten-Netzwerk.
            </p>
            <div className="flex gap-3 sm:gap-4">
              <a href="#" className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-white/5 hover:bg-yellow-600/20 rounded-full transition-colors cursor-pointer" aria-label="LinkedIn">
                <i className="ri-linkedin-line text-base sm:text-lg"></i>
              </a>
              <a href="#" className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-white/5 hover:bg-yellow-600/20 rounded-full transition-colors cursor-pointer" aria-label="Instagram">
                <i className="ri-instagram-line text-base sm:text-lg"></i>
              </a>
              <a href="#" className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-white/5 hover:bg-yellow-600/20 rounded-full transition-colors cursor-pointer" aria-label="YouTube">
                <i className="ri-youtube-line text-base sm:text-lg"></i>
              </a>
            </div>
          </div>

          {/* Column 2: Quick Links */}
          <div>
            <h4 className="text-base sm:text-lg font-light mb-4 sm:mb-6 tracking-wide">Navigation</h4>
            <ul className="space-y-2 sm:space-y-3">
              <li>
                <a href="/" className="text-sm sm:text-base text-white/60 hover:text-yellow-600 transition-colors font-light cursor-pointer">
                  Startseite
                </a>
              </li>
              <li>
                <a href="/masterclass" className="text-sm sm:text-base text-white/60 hover:text-yellow-600 transition-colors font-light cursor-pointer">
                  Dubai Masterclass
                </a>
              </li>
              <li>
                <a href="#bewerbung" className="text-sm sm:text-base text-white/60 hover:text-yellow-600 transition-colors font-light cursor-pointer">
                  Bewerbung
                </a>
              </li>
            </ul>
          </div>

          {/* Column 3: Legal */}
          <div>
            <h4 className="text-base sm:text-lg font-light mb-4 sm:mb-6 tracking-wide">Rechtliches</h4>
            <ul className="space-y-2 sm:space-y-3">
              <li>
                <a href="/impressum" className="text-sm sm:text-base text-white/60 hover:text-yellow-600 transition-colors font-light cursor-pointer">
                  Impressum
                </a>
              </li>
              <li>
                <a href="/datenschutz" className="text-sm sm:text-base text-white/60 hover:text-yellow-600 transition-colors font-light cursor-pointer">
                  Datenschutz
                </a>
              </li>
            </ul>
          </div>

          {/* Column 4: Contact */}
          <div>
            <h4 className="text-base sm:text-lg font-light mb-4 sm:mb-6 tracking-wide">Kontakt</h4>
            <ul className="space-y-3 sm:space-y-4">
              <li className="flex items-start gap-2 sm:gap-3">
                <i className="ri-mail-line text-yellow-600 mt-0.5 flex-shrink-0"></i>
                <a href="mailto:info@dubai-business-club.com" className="text-sm sm:text-base text-white/60 hover:text-yellow-600 transition-colors font-light cursor-pointer break-all">
                  info@dubai-business-club.com
                </a>
              </li>
              <li className="flex items-start gap-2 sm:gap-3">
                <i className="ri-phone-line text-yellow-600 mt-0.5 flex-shrink-0"></i>
                <a href="tel:+4912345678" className="text-sm sm:text-base text-white/60 hover:text-yellow-600 transition-colors font-light cursor-pointer">
                  +49 123 456 78
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-6 sm:pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-xs sm:text-sm text-white/40 font-light text-center sm:text-left">
              © {new Date().getFullYear()} Dubai Business Club. Alle Rechte vorbehalten.
            </p>
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-xs sm:text-sm text-white/40 hover:text-yellow-600 transition-colors font-light cursor-pointer"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
